import {frameworkVersionSchema, routineSourceRefSchema} from './framework-version.types';
import {z} from "zod";
import {permissionObservationsSchema} from "./resource-permissions.types";
import {routineIdentitySchema, routinePlatformSchema, routineResourceKindSchema, routineResourceRequirementSchema} from "./routine-definition.types";
import {firmwareManifestSchema, glassesSoftwareRefSchema} from "./glasses-software.types";
import {candidateVerificationSchema} from './candidate-verification.types';
import type {RoutineLiveProgress} from './routine-progress.types';
import {exclusiveResourceDependenciesSchema} from './resource-reservations.types';
import {testBuildArtifactSourceSchema} from './test-build.types';
import {frameworkIdentitySchema} from './framework-identity.types';
import {resourceIdleContractSchema} from './resource-lifecycle.types';

export {frameworkIdentitySchema} from './framework-identity.types';
export const frameworkBuildSchema = z.object({
  repository: z.string().regex(/^[\w-]+\/[\w.-]+$/),
  headSha: z.string().regex(/^[a-f0-9]{40}$/),
  artifactSource: testBuildArtifactSourceSchema.optional(),
  channel: z.enum(["dev", "staging", "pr", "local"]),
  prNumber: z.number().int().positive().optional(),
  manifest: firmwareManifestSchema.optional(),
  manifestSha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
}).passthrough().superRefine((build, ctx) => {
  if (build.channel === "pr" && !build.prNumber) ctx.addIssue({code: "custom", message: "PR build requires its PR number"});
  if (build.artifactSource && (build.repository !== 'Mentra-Community/Mentra' || !['dev', 'staging'].includes(build.channel)))
    ctx.addIssue({code: 'custom', message: 'Exported artifact source requires a canonical coordinated release'});
});
const frozenFrameworkRequestInputSchema = z.object({
  routineId: routineIdentitySchema,
  routineSource: routineSourceRefSchema,
  minimumFrameworkVersion: frameworkVersionSchema.optional(),
  definitionRevision: z.string().regex(/^[a-f0-9]{40}$/),
  platform: routinePlatformSchema,
  laneId: frameworkIdentitySchema,
  build: frameworkBuildSchema,
  resources: z.array(z.object({
    id: frameworkIdentitySchema,
    kind: routineResourceKindSchema,
    laneId: frameworkIdentitySchema.optional(),
    capabilities: z.array(routineIdentitySchema).max(30).optional(),
    permissionObservations: permissionObservationsSchema.optional(),
    exclusiveDependencies: exclusiveResourceDependenciesSchema.optional(),
    idleContract: resourceIdleContractSchema.optional(),
  }).strict().refine(resource => resource.capabilities === undefined || routineResourceRequirementSchema.safeParse({
    kind: resource.kind, capabilities: resource.capabilities,
  }).success, "Resource capabilities must match their declared kind")),
  policy: z.record(z.unknown()).optional(),
  glassesModel: z.never().optional(),
  glassesStart: glassesSoftwareRefSchema.optional(),
  glassesReturn: glassesSoftwareRefSchema.optional(),
  verification: candidateVerificationSchema.optional(),
}).passthrough();
const recordedInputSchema = frozenFrameworkRequestInputSchema.omit({glassesModel: true})
  .extend({routineSource: routineSourceRefSchema.optional()});
function validateFrameworkRequestIdentity(input: z.infer<typeof recordedInputSchema>, ctx: z.RefinementCtx) {
  if (input.verification && input.verification.sourceRevision !== input.definitionRevision)
    ctx.addIssue({code: 'custom', message: 'Candidate verification source differs from the request'});
  if (input.routineSource && input.routineSource.commit !== input.definitionRevision)
    ctx.addIssue({code: 'custom', message: 'Routine source differs from its definition revision'});
}
function validateFrameworkRequestInput(input: z.infer<typeof recordedInputSchema>, ctx: z.RefinementCtx) {
  validateFrameworkRequestIdentity(input, ctx);
  const glasses = input.resources.some(resource => resource.kind === "glasses");
  if ((glasses && !(input.glassesStart && input.glassesReturn)) || (!glasses && (input.glassesStart || input.glassesReturn)) ||
    (input.glassesStart === undefined) !== (input.glassesReturn === undefined))
    ctx.addIssue({code: "custom", message: "Selected glasses resource requires frozen start and return software references"});
  if (input.glassesStart && input.glassesReturn) {
    if (input.glassesStart.model !== input.glassesReturn.model)
      ctx.addIssue({code: "custom", message: "Starting and return software must name the same selected glasses model"});
    if (!input.build.manifest || input.build.manifestSha256 !== input.build.manifest.sha256 ||
      JSON.stringify(input.glassesReturn.manifest) !== JSON.stringify(input.build.manifest))
      ctx.addIssue({code: "custom", message: "Return glasses software must match the selected build manifest identity"});
  }
}
/** New admission always requires the exact published routine bundle. */
export const frameworkRequestInputSchema = frozenFrameworkRequestInputSchema.superRefine(validateFrameworkRequestInput);
/** Recorded display validates structure and source identity, preserving original metadata and digest.
 * Current execution/software requirements belong to admission, never retrospective display. */
export const recordedFrameworkRequestInputSchema = recordedInputSchema.superRefine(validateFrameworkRequestIdentity);

/** Admin delivery projection; it describes a request without claiming execution evidence. */
export interface FrameworkRequestDisplay {
  requestId: string; hostId?: string; inputSha256?: string; dispatchIntentSha256?: string; routineId: string; platform: string;
  definitionRevision: string; routineSource?: z.infer<typeof routineSourceRefSchema>; minimumFrameworkVersion?: number;
  laneId?: string; build: z.infer<typeof frameworkBuildSchema>;
  state: "awaiting-source" | "awaiting-runner" | "preparing" | "queued" | "accepted" | "running" | "terminal"; terminalStatus?: string;
  createdAt?: string; acceptedAt?: string; reason?: string; reasonAt?: string;
  cancellationRequested?: boolean; cancellationAcknowledged?: boolean;
  assignment?: {jobId: string; hostId: string; laneId: string; actionsRunId: string; actionsJobId: string};
  actionsRuns?: Array<{actionsRunId: string; recordedAt: string}>;
  preparationDisposition?: 'not-run' | 'not-applicable';
  live?: RoutineLiveProgress;
  liveUnavailable?: boolean;
  waiting?: import('./routine-progress.types').RoutineQueuedBlock;
}
